// continuous mode の mic state。continuous は最初に 1 度 ON にしたら以降トグルしない
// ため、mic button は「開始/停止トグル」ではなく発話インジケータとして働く (spec §3.4.1)。
// idle = continuous 未起動 (🎙 タップで開始)、listening = VAD 起動中で発話待ち、
// speaking = ユーザーの発話を検出中 (VAD hearing → 話しているのが UI で分かる)、
// ai-speaking = AI が読み上げ中 (spec 2026-07-28 D8)、
// error = mic 取得失敗など (タップで再試行)。STT〜構造化の進行は partial バブルと
// 「少々お待ちください…」hint 側でも表現する。
// thinking = 聞き取った内容を構造化している (spec 2026-08-04 D11)。従来は別の
// インジケータ (呼吸する点 + 「少々お待ちください…」) で表していたが、画面から
// 文字が消えるのでマイク側に寄せた。合図音 (processingCue) は従来どおり鳴る。
export type MicState = "idle" | "listening" | "speaking" | "thinking" | "ai-speaking" | "error";

type Props = {
  state: MicState;
  onStart: () => void;
  ariaLabel?: string;
  /**
   * 表示サイズ (spec 2026-08-04 D2)。
   * sm = 44px (会話ログ `?log=1` の入力欄まわり) / lg = 112px (前半の画面の主役)。
   * 既定を sm にしてあるので、既存の呼び出しは 1 箇所も変わらない。
   */
  size?: "sm" | "lg";
  /** マイクが使えない環境 (?preflight=skip でマイク拒否) では押せなくする */
  disabled?: boolean;
};

// hint の文字は HIG の最小フォント (11pt) を割らない。sm の text-xs は 12px、
// lg は前半の画面で唯一の文字なので 15px にして離れていても読めるようにする。
const SIZE: Record<"sm" | "lg", { button: string; hint: string }> = {
  sm: { button: "w-11 h-11 text-lg", hint: "text-xs" },
  lg: { button: "w-28 h-28 text-4xl", hint: "text-[15px]" },
};

const STYLE: Record<MicState, { bg: string; label: string }> = {
  idle: { bg: "bg-slate-100 hover:bg-slate-200 border-slate-300", label: "🎙" },
  listening: { bg: "bg-emerald-50 text-emerald-600 border-emerald-200 animate-pulse", label: "🎙" },
  // speaking: 発話検出中は塗りつぶし + 拡大 + グローで「今聞き取れている」ことを強調する。
  // button 側に transition があるので scale はなめらかに変化する。
  speaking: {
    bg: "bg-emerald-500 text-white border-emerald-600 scale-110 shadow-lg shadow-emerald-300/60",
    label: "🎙",
  },
  // 処理中。緑 (あなたの番) とも水色 (AI の声) とも違う藍にし、速く脈打たせて
  // 「働いている」ことを示す。待たせている側なので塗りつぶさない。
  thinking: { bg: "bg-indigo-50 text-indigo-500 border-indigo-200 animate-pulse", label: "🎙" },
  // AI の読み上げ中。緑の脈動 (= あなたの番) と区別できる青系にして脈打たせない。
  // 割り込みはできるので、禁止を示唆する見た目にはしない (spec 2026-07-28 D8)。
  "ai-speaking": { bg: "bg-sky-50 text-sky-600 border-sky-200", label: "🎙" },
  error: { bg: "bg-amber-100 text-amber-700 border-amber-300", label: "!" },
};

const HINT: Record<MicState, string> = {
  idle: "話す",
  listening: "どうぞ",
  speaking: "聞いています",
  thinking: "少々お待ちください",
  "ai-speaking": "AI が話しています",
  error: "もう一度",
};

export function MicButton({ state, onStart, ariaLabel, size = "sm", disabled }: Props) {
  const style = STYLE[state];
  const dim = SIZE[size];
  // どの state でも tap = continuous.start()。start は冪等で、VAD が生きていれば noop
  // (listening / speaking 中の tap)、死んでいれば作り直す (error からの再試行)。
  // push-to-talk 時代の stop 操作は無い。
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={onStart}
        disabled={disabled}
        aria-label={ariaLabel ?? "マイク"}
        className={
          `${dim.button} rounded-full border flex items-center justify-center transition ` +
          (disabled ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed" : style.bg)
        }
      >
        {style.label}
      </button>
      <span className={`${dim.hint} text-slate-500 whitespace-nowrap`}>{HINT[state]}</span>
    </div>
  );
}
